import { message } from "antd";
import moment from "moment";

import { countries, INDIA_NAMES } from "./constants";
import { showAppError } from "./error";

/** Converts the string into title case i.e. only first letter will be capitalized. */
// eslint-disable-next-line no-extend-native, func-names
String.prototype.toTitleCase = function () {
  return this.toLowerCase().replace(/\b(\w)/g, (s) => s.toUpperCase());
};

export default function debounce(func, delay = 300) {
  let timeoutId;
  // eslint-disable-next-line func-names
  return function (...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

export function toFixedNumber(num, precision) {
  return Math.round(num * 10 ** precision) / 10 ** precision;
}

/**
 * This function copies the text to clipboard
 * @param {string} text - text to be copied
 * @param {string} type - type of text (e.g. 'Link', 'Contact no.', 'Email' etc.)
 */
export const handleCopyText = async (text, type = "") => {
  const successMsg = type
    ? `${type} copied successfully`
    : "Copied successfully";
  const errorMsg = "Oops, unable to copy";

  if (!text) {
    showAppError("No text to copy");
    return;
  }

  try {
    await navigator.clipboard.writeText(text?.toString());
    // Show a notification or feedback to the user
    message.success(successMsg);
  } catch (error) {
    showAppError(errorMsg);
  }
};

/**
 * This function returns the country data
 * @param {string} countryCode - country code of the country 2 letter
 * @returns {any} country data
 */
export const getCountryByCode = (countryCode) => {
  if (INDIA_NAMES.includes(countryCode)) {
    return countries?.IN;
  }

  return countries[countryCode.toUpperCase()];
};

/**
 * This function returns the calling code of the country
 * @param {string} countryCode - country code of the country 2 letter
 * @returns string
 */
export const getCallingCode = (countryCode) => {
  if (countryCode === "IN" || countryCode === "india") return "+91";

  const country = countries[countryCode.toUpperCase()];

  return country?.callingCode && country?.callingCode[0]
    ? `+${country?.callingCode[0]}`
    : "";
};

export const getArticle = (phrase) => {
  if (!phrase) return "";

  const word = phrase.trim().toLowerCase();
  const firstChar = word[0];
  const firstWord = word.split(" ")[0]; // Handle multi-word cases

  // Words that start with a vowel but use "a" (e.g., "a university", "a European")
  const aExceptions = new Set([
    "university",
    "european",
    "one",
    "unit",
    "user",
    "unique",
  ]);

  // Words that start with a consonant but use "an" (e.g., "an hour", "an honest person")
  const anExceptions = new Set(["honest", "hour", "heir", "honor"]);

  if (anExceptions.has(firstWord) || /^[aeiou]/.test(firstChar)) {
    return "an";
  }

  if (aExceptions.has(firstWord) || /^[^aeiou]/.test(firstChar)) {
    return "a";
  }

  return "a"; // Default case
};

export const copyToClipboard = (text, msg) => {
  const successMsg = msg || "Copied successfully";
  const errorMsg = "Oops, unable to copy";

  if (navigator.clipboard) {
    navigator.clipboard.writeText(text.toString()).then(
      () => {
        message.success(successMsg);
      },
      (err) => {
        console.log(err);
        message.error(errorMsg);
      }
    );

    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand("copy");
    if (successful) {
      message.success(successMsg);
    } else {
      message.error(errorMsg);
    }
  } catch (err) {
    console.log(err);
    message.error(errorMsg);
  }

  document.body.removeChild(textArea);
};

const irregularPlurals = new Set([
  "children",
  "feet",
  "teeth",
  "geese",
  "mice",
  "men",
  "women",
  "oxen",
  "lice",
  "cacti",
  "radii",
  "fungi",
  "nuclei",
  "syllabi",
  "alumni",
  "data",
  "criteria",
  "phenomena",
]);

const uncountableNouns = new Set([
  "information",
  "equipment",
  "furniture",
  "money",
  "rice",
  "sheep",
  "fish",
  "deer",
  "species",
  "series",
  "news",
]);

export const checkWordForm = (phrase, type) => {
  if (!phrase) return false;

  const words = phrase.toLowerCase().split(" ");
  const lastWord = words[words.length - 1];

  if (irregularPlurals.has(lastWord) || uncountableNouns.has(lastWord)) {
    return true;
  }

  const pluralPatterns = [
    /s$/,
    /xes$/,
    /ches$/,
    /shes$/,
    /oes$/,
    /ies$/,
    /ves$/,
    /men$/,
    /ae$/,
    /i$/,
  ];

  const isInPluralForm = pluralPatterns.some((pattern) =>
    pattern.test(lastWord)
  );
  return type === "plural" ? isInPluralForm : !isInPluralForm;
};

export const convertToSingularOrPlural = (word, targetForm) => {
  if (!word) return word;

  const lowerCaseWord = word.toLowerCase();
  if (checkWordForm(lowerCaseWord, targetForm)) return lowerCaseWord;

  if (targetForm === "plural") {
    if (/[^aeiou]y$/.test(lowerCaseWord))
      return `${lowerCaseWord.slice(0, -1)}ies`;
    if (/(s|x|z|ch|sh)$/.test(lowerCaseWord)) return `${lowerCaseWord}es`;
    return `${lowerCaseWord}s`;
  }

  if (targetForm === "singular") {
    if (/ies$/.test(lowerCaseWord)) return `${lowerCaseWord.slice(0, -3)}y`;
    if (/(ses|xes|zes|ches|shes)$/.test(lowerCaseWord))
      return lowerCaseWord.slice(0, -2);
    if (lowerCaseWord.endsWith("s")) return lowerCaseWord.slice(0, -1);
  }

  return lowerCaseWord;
};

export const pluralizeNoun = (noun) =>
  convertToSingularOrPlural(noun, "plural");

export const getSingularOrPlural = (val, text) =>
  val > 1 ? pluralizeNoun(text) : text;

export const disableFutureDates = (current) => {
  return current && current > moment().endOf("day");
};

export const disablePastDates = (current) => {
  return current && current < moment().startOf("day");
};

/**
 * Extracts the host/hostname from a given URL with comprehensive validation and error handling.
 * Supports various URL formats including HTTP/HTTPS, FTP, custom protocols, IP addresses, and localhost.
 *
 * @param {string} url - The URL string from which to extract the host
 * @param {object} options - Configuration options
 * @param {boolean} options.includeSubdomain - Whether to include subdomains (default: true)
 * @param {boolean} options.throwOnError - Whether to throw error or return null on invalid URL (default: false)
 * @param {boolean} options.normalizeCase - Whether to convert result to lowercase (default: true)
 *
 * @returns {string|null} The extracted host/hostname, or null if URL is invalid and throwOnError is false
 *
 * @throws {Error} If URL is invalid and throwOnError option is true
 *
 * @example
 * // Basic usage
 * extractHostFromUrl('https://www.example.com/path?query=1')
 * // Returns: 'www.example.com'
 *
 * @example
 * // Without subdomain
 * extractHostFromUrl('https://api.sub.example.com/api', { includeSubdomain: false })
 * // Returns: 'example.com'
 *
 * @example
 * // IP address
 * extractHostFromUrl('http://192.168.1.1:8080/path')
 * // Returns: '192.168.1.1'
 *
 * @example
 * // Localhost
 * extractHostFromUrl('http://localhost:3000')
 * // Returns: 'localhost'
 *
 * @example
 * // Invalid URL with error handling
 * extractHostFromUrl('invalid-url', { throwOnError: false })
 * // Returns: null
 *
 * @example
 * // URL without protocol (automatically adds http://)
 * extractHostFromUrl('example.com/path')
 * // Returns: 'example.com'
 */
export const extractHostFromUrl = (url, options = {}) => {
  // Default options
  const {
    includeSubdomain = true,
    throwOnError = false,
    normalizeCase = true,
  } = options;

  // Input validation
  if (!url || typeof url !== "string") {
    const error = new Error("URL must be a non-empty string");
    if (throwOnError) throw error;
    return "";
  }

  // Trim whitespace
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    const error = new Error("URL cannot be empty or only whitespace");
    if (throwOnError) throw error;
    return "";
  }

  try {
    let urlToParse = trimmedUrl;

    // Handle URLs without protocol by adding http://
    if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(urlToParse)) {
      // Check if it looks like a valid domain or IP
      if (/^[a-zA-Z0-9]/.test(urlToParse)) {
        urlToParse = `http://${urlToParse}`;
      } else {
        throw new Error("Invalid URL format");
      }
    }

    // Create URL object for parsing
    const urlObj = new URL(urlToParse);
    let { hostname } = urlObj;

    // Validate that we got a valid hostname
    if (!hostname) {
      throw new Error("Unable to extract hostname from URL");
    }

    // Handle subdomain removal if requested
    if (!includeSubdomain && hostname !== "localhost") {
      // Check if it's an IP address (IPv4 or IPv6)
      const isIPv4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
      const isIPv6 = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/.test(
        hostname
      );

      if (!isIPv4 && !isIPv6) {
        // Split hostname and keep only domain + TLD (last two parts)
        const parts = hostname.split(".");
        if (parts.length > 2) {
          // For domains like api.sub.example.com -> example.com
          // For domains like example.co.uk, this might not work perfectly,
          // but it's a reasonable default behavior
          hostname = parts.slice(-2).join(".");
        }
      }
    }

    // Normalize case if requested
    if (normalizeCase) {
      hostname = hostname.toLowerCase();
    }

    return hostname;
  } catch (error) {
    // Enhanced error message
    const enhancedError = new Error(
      `Failed to extract host from URL "${trimmedUrl}": ${error.message}`
    );

    if (throwOnError) {
      throw enhancedError;
    }

    // Log error for debugging (optional, can be removed in production)
    console.warn("extractHostFromUrl:", enhancedError.message);
    return "";
  }
};

// Enhanced validation functions
export const validateEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

export const validateHost = (host) => {
  // Host should be a valid domain format (e.g., learn.internetlifestylehub.com)
  const hostRegex =
    /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)*[a-zA-Z]{2,}$/;
  return hostRegex.test(host);
};

// RazorpayX (the "Automatic" transfer mode) can only pay out INR; the backend
// rejects anything else, so non-INR withdrawals must be settled manually.
// A missing currency is treated as INR, matching the backend default.
export const isRazorpayXPayoutCurrency = (currency) =>
  (currency || "INR").toUpperCase() === "INR";
